import { useState, useEffect, useCallback } from 'react';
import { Search, Filter, CreditCard, TrendingUp, AlertCircle, CheckCircle, Loader2, ExternalLink } from 'lucide-react';
import { useAuth } from '@clerk/clerk-react';

interface Subscription {
  id: string;
  stripeSubscriptionId: string | null;
  stripeCustomerId: string | null;
  studio: string;
  owner: string;
  plan: 'trial' | 'starter' | 'professional' | 'enterprise';
  status: 'active' | 'past_due' | 'canceled' | 'trialing';
  amount: number;
  interval: 'month' | 'year';
  currentPeriodEnd: string;
  trialEndsAt: string | null;
}

const planColors = {
  trial: 'bg-stone-100 dark:bg-stone-800 text-stone-600 dark:text-stone-400',
  starter: 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400',
  professional: 'bg-sage/10 text-sage',
  enterprise: 'bg-purple-50 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400',
};

const statusColors = {
  active: 'bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400',
  past_due: 'bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400',
  canceled: 'bg-stone-100 dark:bg-stone-800 text-stone-600 dark:text-stone-400',
  trialing: 'bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400',
};

export default function SubscriptionsPage() {
  const { getToken } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchSubscriptions = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'https://api.serenetouch.app';
      const token = await getToken();
      const response = await fetch(`${apiUrl}/admin/subscriptions`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) {
        throw new Error('Failed to fetch subscriptions');
      }
      const data = await response.json();
      if (data.subscriptions) {
        setSubscriptions(data.subscriptions);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load subscriptions');
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  useEffect(() => {
    fetchSubscriptions();
  }, [fetchSubscriptions]);

  const filteredSubscriptions = subscriptions.filter(
    (sub) =>
      sub.studio.toLowerCase().includes(searchQuery.toLowerCase()) ||
      sub.owner.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const totalMRR = subscriptions
    .filter(s => s.status === 'active')
    .reduce((acc, s) => acc + s.amount, 0);

  const activeCount = subscriptions.filter(s => s.status === 'active').length;
  const trialingCount = subscriptions.filter(s => s.status === 'trialing').length;
  const pastDueCount = subscriptions.filter(s => s.status === 'past_due').length;

  return (
    <div className="space-y-6">
      {/* Error Banner */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
          <div>
            <p className="font-medium text-red-700 dark:text-red-300">Error Loading Data</p>
            <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
          </div>
        </div>
      )}

      {/* Loading Overlay */}
      {loading && (
        <div className="fixed inset-0 bg-black/20 dark:bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-stone-900 rounded-xl p-6 flex items-center gap-3 shadow-xl">
            <Loader2 className="w-6 h-6 text-sage animate-spin" />
            <span className="text-stone-700 dark:text-stone-300">Loading subscriptions...</span>
          </div>
        </div>
      )}

      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-stone-800 dark:text-stone-100">Subscriptions</h1>
        <p className="text-stone-500 dark:text-stone-400 mt-1">
          Manage billing and subscription plans
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
        <div className="bg-white dark:bg-stone-900 rounded-xl border border-stone-200 dark:border-stone-800 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-sage/10 rounded-lg flex items-center justify-center">
              <CreditCard className="w-5 h-5 text-sage" />
            </div>
            <div>
              <p className="text-2xl font-bold text-stone-800 dark:text-stone-100">${totalMRR}</p>
              <p className="text-sm text-stone-500">Monthly Revenue</p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-stone-900 rounded-xl border border-stone-200 dark:border-stone-800 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-50 dark:bg-green-900/20 rounded-lg flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-green-500" />
            </div>
            <div>
              <p className="text-2xl font-bold text-stone-800 dark:text-stone-100">{activeCount}</p>
              <p className="text-sm text-stone-500">Active</p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-stone-900 rounded-xl border border-stone-200 dark:border-stone-800 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-50 dark:bg-amber-900/20 rounded-lg flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-amber-500" />
            </div>
            <div>
              <p className="text-2xl font-bold text-stone-800 dark:text-stone-100">{trialingCount}</p>
              <p className="text-sm text-stone-500">In Trial</p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-stone-900 rounded-xl border border-stone-200 dark:border-stone-800 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-red-50 dark:bg-red-900/20 rounded-lg flex items-center justify-center">
              <AlertCircle className="w-5 h-5 text-red-500" />
            </div>
            <div>
              <p className="text-2xl font-bold text-stone-800 dark:text-stone-100">{pastDueCount}</p>
              <p className="text-sm text-stone-500">Past Due</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
          <input
            type="text"
            placeholder="Search subscriptions..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-white dark:bg-stone-900 border border-stone-200 dark:border-stone-700 rounded-xl text-sm text-stone-700 dark:text-stone-300 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-sage/50 focus:border-sage"
          />
        </div>
        <button className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-stone-900 border border-stone-200 dark:border-stone-700 rounded-xl text-stone-600 dark:text-stone-400 hover:bg-stone-50 dark:hover:bg-stone-800 transition-colors">
          <Filter className="w-5 h-5" />
          Filters
        </button>
      </div>

      {/* Mobile Cards */}
      <div className="md:hidden space-y-3">
        {filteredSubscriptions.map((sub) => (
          <div key={sub.id} className="bg-white dark:bg-stone-900 rounded-xl border border-stone-200 dark:border-stone-800 p-4">
            <div className="flex items-start justify-between mb-3">
              <div>
                <p className="font-medium text-stone-700 dark:text-stone-300">{sub.studio}</p>
                <p className="text-sm text-stone-500">{sub.owner}</p>
              </div>
              <div className="text-right">
                <p className="font-semibold text-stone-700 dark:text-stone-300">
                  {sub.amount > 0 ? `$${sub.amount}/${sub.interval}` : 'Free'}
                </p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2 mb-3">
              <span className={`text-xs font-medium px-2 py-1 rounded-full capitalize ${planColors[sub.plan]}`}>
                {sub.plan}
              </span>
              <span className={`text-xs font-medium px-2 py-1 rounded-full ${statusColors[sub.status]}`}>
                {sub.status === 'past_due' ? 'Past Due' : sub.status === 'trialing' ? 'Trial' : sub.status.charAt(0).toUpperCase() + sub.status.slice(1)}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-stone-500">
                {sub.trialEndsAt ? (
                  <span className="text-amber-600">Trial ends {new Date(sub.trialEndsAt).toLocaleDateString()}</span>
                ) : (
                  `Next: ${new Date(sub.currentPeriodEnd).toLocaleDateString()}`
                )}
              </span>
              {sub.stripeSubscriptionId && (
                <a
                  href={`https://dashboard.stripe.com/subscriptions/${sub.stripeSubscriptionId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-sm text-sage hover:text-sage/80 font-medium"
                >
                  View
                  <ExternalLink className="w-3 h-3" />
                </a>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Desktop Table */}
      <div className="hidden md:block bg-white dark:bg-stone-900 rounded-2xl border border-stone-200 dark:border-stone-800 overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-stone-200 dark:border-stone-800">
              <th className="text-left px-6 py-4 text-sm font-medium text-stone-500 dark:text-stone-400">Studio</th>
              <th className="text-left px-6 py-4 text-sm font-medium text-stone-500 dark:text-stone-400">Plan</th>
              <th className="text-left px-6 py-4 text-sm font-medium text-stone-500 dark:text-stone-400">Status</th>
              <th className="text-left px-6 py-4 text-sm font-medium text-stone-500 dark:text-stone-400">Amount</th>
              <th className="text-left px-6 py-4 text-sm font-medium text-stone-500 dark:text-stone-400">Next Billing</th>
              <th className="text-left px-6 py-4 text-sm font-medium text-stone-500 dark:text-stone-400">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredSubscriptions.map((sub) => (
              <tr key={sub.id} className="border-b border-stone-100 dark:border-stone-800 last:border-0 hover:bg-stone-50 dark:hover:bg-stone-800/50 transition-colors">
                <td className="px-6 py-4">
                  <div>
                    <p className="font-medium text-stone-700 dark:text-stone-300">{sub.studio}</p>
                    <p className="text-sm text-stone-500">{sub.owner}</p>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <span className={`text-xs font-medium px-2 py-1 rounded-full capitalize ${planColors[sub.plan]}`}>
                    {sub.plan}
                  </span>
                </td>
                <td className="px-6 py-4">
                  <span className={`text-xs font-medium px-2 py-1 rounded-full ${statusColors[sub.status]}`}>
                    {sub.status === 'past_due' ? 'Past Due' : sub.status === 'trialing' ? 'Trial' : sub.status.charAt(0).toUpperCase() + sub.status.slice(1)}
                  </span>
                </td>
                <td className="px-6 py-4 text-stone-700 dark:text-stone-300">
                  {sub.amount > 0 ? `$${sub.amount}/${sub.interval}` : 'Free'}
                </td>
                <td className="px-6 py-4 text-stone-500">
                  {sub.trialEndsAt ? (
                    <span className="text-amber-600">Trial ends {new Date(sub.trialEndsAt).toLocaleDateString()}</span>
                  ) : (
                    new Date(sub.currentPeriodEnd).toLocaleDateString()
                  )}
                </td>
                <td className="px-6 py-4">
                  <div className="flex items-center gap-2">
                    {sub.stripeSubscriptionId ? (
                      <a
                        href={`https://dashboard.stripe.com/subscriptions/${sub.stripeSubscriptionId}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-sm text-sage hover:text-sage/80 font-medium"
                      >
                        View
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    ) : (
                      <span className="text-sm text-stone-400">No subscription</span>
                    )}
                    {sub.status === 'past_due' && sub.stripeSubscriptionId && (
                      <a
                        href={`https://dashboard.stripe.com/subscriptions/${sub.stripeSubscriptionId}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-red-500 hover:text-red-600 font-medium"
                      >
                        Manage
                      </a>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
